"use client";

import { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { ScrollArea } from "@/components/ui/scroll-area";
import { splitStages, type StageItem } from "./types";
import { LockedStageCard } from "./locked-card";
import { CustomStageCard, type StageMappingControl } from "./custom-card";
import { StageCreatePill } from "./create-pill";

function SortableStageItem({
  stage,
  displayIndex,
  onUpdate,
  onRemove,
  atsLinked,
  mapping,
}: {
  stage: StageItem;
  displayIndex: number;
  onUpdate: (id: string, patch: Partial<StageItem>) => void;
  onRemove: (id: string) => void;
  atsLinked?: boolean;
  mapping?: StageMappingControl;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.35 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full relative z-[1]" data-stage-node="">
      <CustomStageCard
        stage={stage}
        displayIndex={displayIndex}
        onUpdate={onUpdate}
        onRemove={onRemove}
        atsLinked={atsLinked}
        mapping={mapping}
        dragHandleProps={{ ...attributes, ...listeners } as React.HTMLAttributes<HTMLButtonElement>}
      />
    </div>
  );
}

export function SortableStageList({
  stages,
  onUpdate,
  onRemove,
  onAdd,
  onReorder,
  atsLinked = false,
  renderMapping,
}: {
  stages: StageItem[];
  onUpdate: (id: string, patch: Partial<StageItem>) => void;
  onRemove: (id: string) => void;
  onAdd: (name: string, color: string, description: string) => void;
  onReorder: (reordered: StageItem[]) => void;
  /**
   * ATS-linked job: stage deletion is disabled on each card and the add-stage
   * pill is hidden (stages mirror the ATS) — users map each stage to a remote
   * stage via the edit panel instead.
   */
  atsLinked?: boolean;
  /** Per-stage remote-ATS-stage mapping control shown in the card edit panel. */
  renderMapping?: (stage: StageItem) => StageMappingControl | undefined;
}) {
  const { leading, editable, trailing } = splitStages(stages);
  const usedColors = useMemo(() => new Set(stages.map((s) => s.color)), [stages]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Trim the vertical connector line so it spans only between the first
  // and last stage cards (centered on their vertical midpoints) — never
  // above the first card or below the last.
  const listRef = useRef<HTMLDivElement>(null);
  const [lineBounds, setLineBounds] = useState<{ top: number; bottom: number } | null>(null);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const nodes = new Set<Element>();
    const recompute = () => {
      const items = el.querySelectorAll<HTMLElement>("[data-stage-node]");
      if (items.length === 0) {
        setLineBounds(null);
        return;
      }
      const containerRect = el.getBoundingClientRect();
      const firstRect = items[0].getBoundingClientRect();
      const lastRect = items[items.length - 1].getBoundingClientRect();
      setLineBounds({
        top: firstRect.top - containerRect.top + firstRect.height / 2,
        bottom: containerRect.bottom - lastRect.bottom + lastRect.height / 2,
      });
      // Observe each item so height changes (e.g. editing a card) retrigger.
      for (const item of items) {
        if (!nodes.has(item)) {
          nodes.add(item);
          ro.observe(item);
        }
      }
    };
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    recompute();
    return () => ro.disconnect();
  }, [stages]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIdx = editable.findIndex((s) => s.id === active.id);
      const newIdx = editable.findIndex((s) => s.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return;

      const reordered = [...editable];
      const [moved] = reordered.splice(oldIdx, 1);
      reordered.splice(newIdx, 0, moved);

      onReorder([...leading, ...reordered, ...trailing]);
    },
    [editable, leading, trailing, onReorder],
  );

  const handleDragCancel = useCallback(() => setActiveId(null), []);

  const activeStage = activeId ? editable.find((s) => s.id === activeId) : null;
  const activeDisplayIndex = activeStage
    ? leading.length + editable.indexOf(activeStage) + 1
    : 0;

  return (
    <ScrollArea className="h-full">
      <div ref={listRef} className="flex flex-col items-center w-full relative lg:p-6" style={{ gap: 30 }}>
        {lineBounds && (
          <div
            aria-hidden="true"
            className="absolute left-1/2 w-px bg-foreground/25 dark:bg-[#343436] -translate-x-1/2 pointer-events-none"
            style={{ top: lineBounds.top, bottom: lineBounds.bottom }}
          />
        )}

        {leading.map((stage, i) => (
          <div key={stage.id} className="w-full relative z-[1]" data-stage-node="">
            <LockedStageCard stage={stage} displayIndex={i + 1} />
          </div>
        ))}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={editable.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {editable.map((stage, i) => (
              <SortableStageItem
                key={stage.id}
                stage={stage}
                displayIndex={leading.length + i + 1}
                onUpdate={onUpdate}
                onRemove={onRemove}
                atsLinked={atsLinked}
                mapping={renderMapping?.(stage)}
              />
            ))}
          </SortableContext>

          {mounted && createPortal(
            <DragOverlay dropAnimation={null}>
              {activeStage ? (
                <CustomStageCard
                  stage={activeStage}
                  displayIndex={activeDisplayIndex}
                  onUpdate={() => {}}
                  onRemove={() => {}}
                />
              ) : null}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>

        {!atsLinked && <StageCreatePill usedColors={usedColors} onAdd={onAdd} />}

        {trailing.map((stage, i) => (
          <div key={stage.id} className="w-full relative z-[1]" data-stage-node="">
            <LockedStageCard
              stage={stage}
              displayIndex={leading.length + editable.length + i + 1}
            />
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}
